import { useQuery } from '@apollo/client';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import { Queries } from '@shared/api';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';

interface DmChannelUsers {
  users: API.DmChannelUsersQuery['listChannels'][number]['channelMemberships'][number]['user'][];
  loading: boolean;
}

/**
 * Fetches users for all DM channels in a single batched query,
 * then returns the users for the specified channelId.
 *
 * This replaces per-channel `useChannelUsers` calls for DM avatars,
 * reducing N queries down to 1.
 */
export function useDmChannelUsers(channelId: number | undefined): DmChannelUsers {
  const { data, loading } = useQuery<API.DmChannelUsersQuery, API.DmChannelUsersQueryVariables>(
    Queries.channel.dmChannelUsers,
    {
      variables: { input: { type: 'DM' } },
      skip: isNil(channelId),
    },
  );

  return useMemo(() => {
    const channel = data?.listChannels?.find(c => c.id === channelId);
    return {
      users: channel?.channelMemberships?.map(m => m.user).filter(notNil) ?? [],
      loading,
    };
  }, [data?.listChannels, channelId, loading]);
}
